import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    IMultiSelectOption,
    InfiniteScroll,
    inject,
    NothingHere,
    TasksStore,
    Title,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsCardHeading } from '@react-icons/all-files/bs/BsCardHeading';
import { RouteComponentProps } from 'react-router';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { DisplayFiltersStore } from '../DisplayFilters/DisplayFiltersStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { DisplayViewTypes } from '../DisplayFilters/DisplayViewSelector';
import { DisplayCard } from './DisplayCard';
import { DisplayHeader } from './DisplayHeader';
import { DisplayListMap } from './DisplayListMap';
import { DisplayListNav } from './DisplayListNav';
import { DisplayRow } from './DisplayRow';
import { DisplaysStore } from './DisplaysStore';
import { IWhereClause } from '../DisplayFilters/WhereFilter';

export interface IFavoritesListProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class FavoritesList extends React.Component<IFavoritesListProps> {
    public constructor(props: IFavoritesListProps) {
        super(props);

        this.vm = new DisplayListViewModel({
            onChange: this.onChange,
            onClear: this.onClear,
            sort: this.displayFiltersStore.storage.sort,
            //where: props.displayFiltersStore.storage.where,
            favorites: true,
            columns: this.accountStore.storage.displayTableColumns,
            viewType: this.displayFiltersStore.storage.viewType,
            interval: 30000,
            subscribe: false,
        });
    }

    public async componentDidMount() {
        try {
            await this.vm.loadMoreAsync();
            if (this.vm.viewType == DisplayViewTypes.MapView) await this.vm.loadGpsAsync();
        } catch (e: any) {
            this.appStore.setError(e);
        }
    }

    public componentWillUnmount() {
        this.vm.dispose();
    }

    public render() {
        const { hasMore, busy, loadMoreAsync, displays, removeDisplay } = this.vm;

        return (
            <div className="container-fluid p-0 h-100">
                <Title>{this.vm.total >= 0 ? this.vm.total + ' On ' : ''}Watchlist</Title>

                <DisplayListNav vm={this.vm} hideInput />

                {this.vm.viewType != DisplayViewTypes.MapView && (
                    <InfiniteScroll
                        throttle={100}
                        threshold={300}
                        hasMore={hasMore}
                        isLoading={busy}
                        container={this.appStore.mainContainer}
                        onLoadMore={loadMoreAsync}
                    >
                        {(this.vm.viewType == DisplayViewTypes.TableLarge ||
                            this.vm.viewType == DisplayViewTypes.TableSmall) && (
                            <table className={this.tableClassName + ' table-responsive-sm'}>
                                <DisplayHeader vm={this.vm} />

                                <tbody>
                                    {displays.map((d, idx) => (
                                        <DisplayRow
                                            vm={this.vm}
                                            key={d.serialNumber}
                                            display={d}
                                            rowNumber={idx + 1}
                                            history={this.props.history}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {this.vm.viewType == DisplayViewTypes.CardView && (
                            <div className="container-fluid">
                                <div className="row">
                                    {displays.map(d => (
                                        <DisplayCard key={d.serialNumber} display={d} />
                                    ))}
                                </div>
                            </div>
                        )}

                        <NothingHere
                            empty={!displays.length}
                            forceLoaderOnLoad
                            icon={<BsCardHeading />}
                            loading={busy}
                            label="No Displays Returned"
                            loadingMessage="Loading Displays..."
                        />
                    </InfiniteScroll>
                )}

                {this.vm.viewType == DisplayViewTypes.MapView && (
                    <DisplayListMap displays={displays} vm={this.vm} history={this.props.history} />
                )}
            </div>
        );
    }

    @bind
    @action
    private onChange(
        displayFilter: Mri.Web.Portal.Controllers.DisplayFilters.DisplayFilterModel,
        displayEventFilter: Mri.DisplayEvents.DisplayEventFilter,
        sort: string[],
        where: IWhereClause[],
        columns: IMultiSelectOption<string | Mri.WebApi.MeasurementId>[]
    ) {
        this.displayFiltersStore.storage.sort = sort;
    }

    @bind
    private onClear() {
        // do nothing
    }

    @computed
    private get tableClassName() {
        let className = 'table table-striped fixed-header';

        if (this.vm.viewType == DisplayViewTypes.TableSmall) className += ' table-sm';

        return className;
    }

    private vm: DisplayListViewModel;
    @inject
    private displayFiltersStore!: DisplayFiltersStore;
    @inject
    private displaysStore!: DisplaysStore;
    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
}
